import { loadRouteDataOrNotFound } from '@/lib/route-loader'
import { customPageQueries } from '@/lib/services/custom-pages/custom-pages.query'
import type { CustomPage } from '@/lib/services/custom-pages/custom-pages.types'
import type { QueryClient } from '@tanstack/react-query'
import { notFound } from '@tanstack/react-router'

/** The part of the router context these loaders read. */
interface CustomPageRouteContext {
  ability: { can(action: string, subject: string): boolean }
  queryClient: Pick<QueryClient, 'ensureQueryData'>
}

interface CustomPageRouteArgs {
  context: CustomPageRouteContext
  params: { orgId: number }
}

function isGlobalOrganization(orgId: number) {
  return orgId === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)
}

export async function loadCustomPagesRoute({ context, params }: CustomPageRouteArgs) {
  if (!context.ability.can('list', 'custom-pages')) throw notFound()

  // The global organization has no storefront; its page asks for a florist account instead.
  if (!isGlobalOrganization(params.orgId)) {
    await context.queryClient.ensureQueryData(
      customPageQueries.index({ organizationId: params.orgId })
    )
  }

  return { isReadOnly: !context.ability.can('create', 'custom-pages') }
}

export function loadCreateCustomPageRoute({ context, params }: CustomPageRouteArgs) {
  if (!context.ability.can('create', 'custom-pages') || isGlobalOrganization(params.orgId)) {
    throw notFound()
  }

  return { isReadOnly: false }
}

export async function loadCustomPageRoute({
  context,
  params,
}: {
  context: CustomPageRouteContext
  params: { orgId: number; customPageId: number }
}): Promise<{ customPage: CustomPage; isReadOnly: boolean }> {
  if (!context.ability.can('view', 'custom-pages')) throw notFound()

  const customPage = await loadRouteDataOrNotFound(
    () =>
      context.queryClient.ensureQueryData(
        customPageQueries.show({
          organizationId: params.orgId,
          customPageId: params.customPageId,
        })
      ),
    () => notFound()
  )

  // Florist roles can list and view pages; only staff who can edit them get the editor.
  return { customPage, isReadOnly: !context.ability.can('edit', 'custom-pages') }
}
